import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Tabs, Tab, Card, Spinner } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../services/api';
const PaymentModal = ({ show, onHide, onSuccess }) => {
  const [paymentMethod, setPaymentMethod] = useState('binance');
  const [amount, setAmount] = useState(100);
  const [currency, setCurrency] = useState('USD');
  const [coin, setCoin] = useState('USDT');
  const [transactionId, setTransactionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [revolutUrl, setRevolutUrl] = useState('');
  const [binanceInfo, setBinanceInfo] = useState(null);
  const [binancePaymentSaved, setBinancePaymentSaved] = useState(false);
  const [bankSent, setBankSent] = useState(false);
  const handleRevolutPayment = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/payment-integration/revolut/create', {
        amount,
        currency
      });
      setRevolutUrl(response.data.payment_url);
      window.open(response.data.payment_url, '_blank');
      if (onSuccess) onSuccess();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create Revolut payment');
    } finally {
      setLoading(false);
    }
  };
  const handleBinanceGenerate = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/payment-integration/binance/info', {
        amount,
        currency,
        coin
      });
      setBinanceInfo(response.data);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to generate Binance payment info');
    } finally {
      setLoading(false);
    }
  };
  const handleBinanceConfirmPayment = async () => {
    setError('');
    setLoading(true);
    try {
      if (binanceInfo.memo) {
        await api.post('/payment-integration/binance/confirm-payment', {
          memo: binanceInfo.memo,
          amount: binanceInfo.amount,
          coin: binanceInfo.coin,
          currency: binanceInfo.currency
        });
      } else {
        if (!transactionId || !transactionId.trim()) {
          setError('Please enter Transaction ID');
          setLoading(false);
          return;
        }
        await api.post('/payment-integration/binance/confirm-payment', {
          transaction_id: transactionId.trim(),
          amount: amount,
          coin: coin,
          currency: currency
        });
      }
      setBinancePaymentSaved(true);
      if (onSuccess) onSuccess();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save payment');
    } finally {
      setLoading(false);
    }
  };
  const handleBankInvoice = async () => {
    setError('');
    setLoading(true);
    try {
      await api.post('/payment-integration/bank/invoice', {
        amount,
        currency
      });
      setBankSent(true);
      if (onSuccess) onSuccess();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send bank invoice');
    } finally {
      setLoading(false);
    }
  };
  const resetState = () => {
    setRevolutUrl('');
    setBinanceInfo(null);
    setBinancePaymentSaved(false);
    setBankSent(false);
    setTransactionId('');
    setError('');
  };
  return (
    <Modal show={show} onHide={onHide} size="lg" onEntering={resetState}>
      <Modal.Header closeButton>
        <Modal.Title>Top Up Balance</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Amount</Form.Label>
          <Form.Control type="number" value={amount} onChange={(e) => setAmount(e.target.value)} min="10" />
          <Form.Text className="text-muted">Minimum amount: $10</Form.Text>
        </Form.Group>
        <Tabs activeKey={paymentMethod} onSelect={(k) => setPaymentMethod(k)} className="mb-3">
          <Tab eventKey="binance" title="₿ Binance (Crypto)">
            <Card className="p-3">
              <h5>Pay with Cryptocurrency</h5>
              <Form.Group className="mb-3">
                <Form.Label>Select Coin</Form.Label>
                <Form.Select value={coin} onChange={(e) => setCoin(e.target.value)} disabled={binanceInfo !== null}>
                  <option value="USDT">USDT (Tether)</option>
                </Form.Select>
              </Form.Group>
              {!binanceInfo ? (
                <Button variant="warning" onClick={handleBinanceGenerate} disabled={loading || !amount}>
                  {loading ? <Spinner animation="border" size="sm" /> : 'Generate Payment Address'}
                </Button>
              ) : (
                <div>
                  {binanceInfo.memo && (
                    <Alert variant="info">
                      <strong>Important:</strong> You must include the memo/tag when sending payment!
                    </Alert>
                  )}
                  <div className="text-center mb-3">
                    <QRCodeSVG value={binanceInfo.qr_data} size={200} />
                  </div>
                  <Card className="mb-3 bg-light">
                    <Card.Body>
                      <p><strong>Coin:</strong> {binanceInfo.coin}</p>
                      <p><strong>Network:</strong> {binanceInfo.network}</p>
                      <p><strong>Amount:</strong> {binanceInfo.amount} {binanceInfo.coin}</p>
                      <p><strong>Address:</strong><br />
                        <code style={{ fontSize: '12px', wordBreak: 'break-all' }}>{binanceInfo.address}</code>
                        <Button size="sm" variant="outline-primary" className="ms-2" onClick={() => navigator.clipboard.writeText(binanceInfo.address)}>
                          Copy
                        </Button>
                      </p>
                      {binanceInfo.memo && (
                        <p><strong style={{ color: 'red' }}>MEMO/TAG (REQUIRED):</strong><br />
                          <code style={{ fontSize: '16px', fontWeight: 'bold' }}>{binanceInfo.memo}</code>
                          <Button size="sm" variant="outline-primary" className="ms-2" onClick={() => navigator.clipboard.writeText(binanceInfo.memo)}>
                            Copy
                          </Button>
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                  {binanceInfo.warning && (
                    <Alert variant="warning">
                      {binanceInfo.warning}
                    </Alert>
                  )}
                  <h6>Instructions:</h6>
                  <ol style={{ fontSize: '14px' }}>
                    {binanceInfo.instructions.map((instruction, idx) => (
                      <li key={idx}>{instruction}</li>
                    ))}
                  </ol>
                  {!binanceInfo.memo && (
                    <Form.Group className="mb-3">
                      <Form.Label>Transaction ID (TX Hash)</Form.Label>
                      <Form.Control 
                        type="text" 
                        placeholder="Enter your transaction ID / TX hash" 
                        value={transactionId} 
                        onChange={(e) => setTransactionId(e.target.value)}
                        disabled={binancePaymentSaved}
                      />
                      <Form.Text className="text-muted">
                        After sending payment, copy the Transaction ID from your wallet and paste it here
                      </Form.Text>
                    </Form.Group>
                  )}
                  {!binancePaymentSaved ? (
                    <div className="d-grid mt-3">
                      <Button variant="success" size="lg" onClick={handleBinanceConfirmPayment} disabled={loading}>
                        {loading ? <Spinner animation="border" size="sm" /> : '✓ Confirm Payment'}
                      </Button>
                    </div>
                  ) : (
                    <Alert variant="success" className="mt-3">
                      ✓ Payment confirmed! We will notify you once the transaction is validated.
                    </Alert>
                  )}
                </div>
              )}
            </Card>
          </Tab>
          <Tab eventKey="bank" title="🏦 Bank Transfer">
            <Card className="p-3">
              <h5>Bank Transfer</h5>
              <p className="text-muted">Receive invoice with bank details via email</p>
              <Form.Group className="mb-3">
                <Form.Label>Currency</Form.Label>
                <Form.Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </Form.Select>
              </Form.Group>
              <Alert variant="warning">
                <strong>Note:</strong> The amount can be arbitrary, but we recommend at least $100 for initial deposit.
              </Alert>
              {bankSent ? (
                <Alert variant="success">
                  Invoice has been sent to your email. Please check your inbox.
                </Alert>
              ) : (
                <Button variant="info" onClick={handleBankInvoice} disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" /> : 'Send Invoice to Email'}
                </Button>
              )}
            </Card>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};
export default PaymentModal;